import { create } from '@bufbuild/protobuf'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import {
    GrpcClientsContextValue,
    useGrpcClients,
} from '@/contexts/GrpcClientContext'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { TimeTrackingRole } from '@/generated/Enumerations/TimeTrackingRole_pb'
import {
    IdentityUserFiltersDto,
    IdentityUserFiltersDtoSchema,
} from '@/generated/Messages/Filters_pb'
import { assertNotNullish } from '@/helper/assertNotNullish'
import { loadAllIter } from '@/helper/loadAllIter'
import {
    listAllQueryKeyPrefix,
    listQueryKeyPrefix,
    usersQueryKeyPrefix,
} from '@/helper/queryKeyPrefixes'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

export const usersListQueryKeyPrefix = [usersQueryKeyPrefix] as const

export const usersQueryOptions = (
    clients: Partial<GrpcClientsContextValue> | null,
    filter: Partial<OmitDeepMessageFields<IdentityUserFiltersDto>> = {},
    loadAll = false
) =>
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryOptions({
        queryKey:
            loadAll ?
                [...usersListQueryKeyPrefix, listAllQueryKeyPrefix, filter]
            :   [...usersListQueryKeyPrefix, listQueryKeyPrefix, filter],
        queryFn: async ({ signal }) => {
            assertNotNullish(clients?.user)

            const internalFilter = create(IdentityUserFiltersDtoSchema, {
                deletedItemSelection:
                    filter.deletedItemSelection ?? DeletedItemSelection.BOTH,
                ids: filter.ids ?? [],
                projectIds: filter.projectIds ?? [],
                sorting: filter.sorting ?? [],
                pagination: filter.pagination,
                role: TimeTrackingRole.UNSPECIFIED,
                searching: filter.searching ?? [],
                searchOperator: filter.searchOperator,
            })

            if (!loadAll) {
                const response = await clients.user.getUsers(internalFilter, {
                    signal,
                })

                return response
            }

            const users = (
                await Array.fromAsync(
                    loadAllIter(
                        async f => {
                            const { users: data, pagination } =
                                (await clients.user?.getUsers(f, {
                                    signal,
                                })) ?? {}

                            return { data, pagination }
                        },
                        internalFilter,
                        1000
                    )
                )
            ).flat()

            return {
                pagination: { totalItemCount: users.length },
                users,
            }
        },
        enabled: !clients?.user,
    })

export const useUsers = (
    filter: Partial<OmitDeepMessageFields<IdentityUserFiltersDto>> = {},
    loadAll = false
) => {
    const clients = useGrpcClients()

    return useSuspenseQuery(usersQueryOptions(clients, filter, loadAll))
}
